export const SITE_URL = 'https://whenwemeet.xyz';
export const SITE_NAME = 'WhenWeMeet?';
export const SITE_LOCALE = 'en_US';

export const OG_IMAGE_WIDTH = 1200;
export const OG_IMAGE_HEIGHT = 800;

export const DEFAULT_DESCRIPTION =
  'WhenWeMeet helps you find the time that works for everyone. Share a single link, collect availability, and pick the best time without calendar conflicts.';

export type RouteSeoKey =
  | '/'
  | '/app'
  | '/link-devices'
  | '/support'
  | '/privacy'
  | '/terms'
  | '/s'
  | '/get-app'
  | '/404';

export type RouteSeoConfig = {
  title: string;
  description: string;
  path: string;
  robots?: string;
  ogType?: string;
};

export const ROUTE_SEO: Record<RouteSeoKey, RouteSeoConfig> = {
  '/': {
    title: 'WhenWeMeet? - Group Meeting Scheduling',
    description: DEFAULT_DESCRIPTION,
    path: '/',
    ogType: 'website',
  },
  '/app': {
    title: 'WhenWeMeet Mobile App - iOS & Android',
    description:
      'Download WhenWeMeet for iOS and Android. Create meeting planners, share availability, and find the perfect time for your group — free and cross-platform.',
    path: '/app',
    ogType: 'website',
  },
  '/link-devices': {
    title: 'Link Devices - WhenWeMeet?',
    description:
      'Link your WhenWeMeet web and mobile devices to sync meeting planners and availability across platforms.',
    path: '/link-devices',
    ogType: 'website',
  },
  '/support': {
    title: 'Help & Support - WhenWeMeet?',
    description:
      'Get help with WhenWeMeet. Contact support, report bugs, send feedback, or find answers to common questions about group scheduling.',
    path: '/support',
    ogType: 'website',
  },
  '/privacy': {
    title: 'Privacy Policy - WhenWeMeet?',
    description:
      'Read the WhenWeMeet privacy policy to learn how we handle your data when you use our group meeting scheduling service.',
    path: '/privacy',
    ogType: 'website',
  },
  '/terms': {
    title: 'Terms of Service - WhenWeMeet?',
    description:
      'Read the WhenWeMeet terms of service governing use of our group meeting scheduling web and mobile applications.',
    path: '/terms',
    ogType: 'website',
  },
  '/s': {
    title: 'Meeting Planner - WhenWeMeet?',
    description: DEFAULT_DESCRIPTION,
    path: '/s',
    robots: 'noindex, nofollow',
    ogType: 'website',
  },
  '/get-app': {
    title: 'Get the App - WhenWeMeet?',
    description:
      'Redirecting to download WhenWeMeet on the App Store or Google Play.',
    path: '/get-app',
    robots: 'noindex, nofollow',
    ogType: 'website',
  },
  '/404': {
    title: 'Page Not Found - WhenWeMeet?',
    description: 'The page you are looking for could not be found.',
    path: '/404',
    robots: 'noindex, nofollow',
    ogType: 'website',
  },
};

/** Public routes included in sitemap and prerender build */
export const INDEXABLE_ROUTES = [
  '/',
  '/app',
  '/link-devices',
  '/support',
  '/privacy',
  '/terms',
] as const satisfies readonly RouteSeoKey[];

export type IndexableRoute = (typeof INDEXABLE_ROUTES)[number];

export type SitemapMeta = {
  changefreq: 'weekly' | 'monthly' | 'yearly';
  priority: string;
};

export const SITEMAP_META: Record<IndexableRoute, SitemapMeta> = {
  '/': { changefreq: 'weekly', priority: '1.0' },
  '/app': { changefreq: 'monthly', priority: '0.8' },
  '/link-devices': { changefreq: 'monthly', priority: '0.7' },
  '/support': { changefreq: 'yearly', priority: '0.6' },
  '/privacy': { changefreq: 'yearly', priority: '0.5' },
  '/terms': { changefreq: 'yearly', priority: '0.5' },
};

export function getCanonicalUrl(path: string): string {
  const normalized = path.startsWith('/') ? path : `/${path}`;
  if (normalized === '/') {
    return SITE_URL;
  }
  return `${SITE_URL}${normalized}`;
}

export function getRouteSeo(route: RouteSeoKey): RouteSeoConfig {
  return ROUTE_SEO[route];
}
